import { useState, useEffect, useCallback } from "react";
import {
  Phone, Delete, Loader2, Store, PhoneCall, Info, Sparkles,
} from "lucide-react";
import { useCall } from "@/context/CallContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

/** Normalise un numéro FR vers E.164 (miroir du backend `to_e164_fr`). */
export function toE164Fr(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/[^\d+]/g, "");
  if (s.startsWith("+")) return s.length >= 8 ? s : null;
  if (s.startsWith("0033")) return "+" + s.slice(2);
  if (s.startsWith("33") && s.length >= 11) return "+" + s;
  if (s.startsWith("0") && s.length === 10) return "+33" + s.slice(1);
  if (s.length === 9) return "+33" + s;
  return null;
}

/** Affichage lisible d'un numéro FR (+33 1 62 29 08 15). */
function prettyFr(e164) {
  if (!e164) return "";
  if (e164.startsWith("+33") && e164.length === 12) {
    const n = e164.slice(3);
    return `+33 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`;
  }
  return e164;
}

const PAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0", "back"];

const EMPTY_SPOT = {
  enseigne: "", type: "supermarche", ville: "", arrondissement: "", rue: "",
  nom_donne: "", dept: "", territoire_id: "", flux: "", consignes: "",
  civilite: "", nom: "", prenom: "", fonction: "", email: "",
};

/**
 * Bouton + dialog « Composer un numéro » :
 * - saisie/pavé numérique, validation E.164 FR ;
 * - appel direct (sans spot) OU enregistrement en nouveau spot (Banque de spots)
 *   avec toutes les infos utiles, le numéro devenant le téléphone du contact.
 */
export function DialerButton({
  triggerLabel = "Composer un numéro",
  triggerClassName = "",
  size = "default",
  variant = "default",
  onSpotCreated,
  "data-testid": testId = "dialer-open-btn",
}) {
  const { startCall, config } = useCall();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [label, setLabel] = useState("");
  const [asSpot, setAsSpot] = useState(false);
  const [f, setF] = useState(EMPTY_SPOT);
  const [territoires, setTerritoires] = useState([]);
  const [saving, setSaving] = useState(false);

  const e164 = toE164Fr(number);
  const valid = !!e164;

  useEffect(() => {
    if (!open) return;
    setNumber(""); setLabel(""); setAsSpot(false); setF(EMPTY_SPOT);
    api.get("/territoires").then((r) => setTerritoires(r.data || [])).catch(() => {});
  }, [open]);

  const setSpot = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const pad = (k) => setNumber((n) => (k === "back" ? n.slice(0, -1) : n + k));

  const spotPreview = [f.enseigne, `${f.ville}${f.arrondissement ? " " + f.arrondissement : ""}`, f.rue, f.nom_donne]
    .filter(Boolean).join(" ").toUpperCase();

  const launch = useCallback(async (force = false) => {
    if (!valid) { toast.error("Numéro invalide. Format attendu : 06…, 01…, +33…"); return; }
    if (!config?.ready) { toast.info("Téléphonie en attente : le numéro Twilio n'est pas encore actif."); return; }

    // Mode "enregistrer comme spot" : on crée d'abord le spot, puis on appelle.
    if (asSpot) {
      if (!f.enseigne || !f.ville) { toast.error("Enseigne et ville obligatoires pour créer un spot."); return; }
      setSaving(true);
      try {
        const payload = {
          enseigne: f.enseigne, type: f.type, ville: f.ville, arrondissement: f.arrondissement,
          rue: f.rue, nom_donne: f.nom_donne, dept: f.dept, territoire_id: f.territoire_id || undefined,
          consignes: f.consignes, status: "jamais_appele",
          flux: f.flux ? { mode: "global", global_value: Number(f.flux), source: "estime_cdp" } : {},
          contact_principal: {
            civilite: f.civilite || undefined, nom: f.nom || undefined, prenom: f.prenom || undefined,
            fonction: f.fonction || undefined, telephone: e164, email: f.email || undefined,
          },
          force,
        };
        const { data } = await api.post("/spots", payload);
        toast.success(`Spot créé : ${data.normalized_name}`);
        onSpotCreated?.(data);
        const contactName = [f.prenom, f.nom].filter(Boolean).join(" ") || label || data.normalized_name;
        setOpen(false);
        await startCall({ spotId: data.id, toNumber: e164, contactName, spotName: data.normalized_name });
      } catch (err) {
        if (err?.response?.status === 409) {
          if (window.confirm(err.response.data.detail + "\n\nCréer quand même ?")) { setSaving(false); return launch(true); }
        } else {
          toast.error(err?.response?.data?.detail || "Impossible de créer le spot.");
        }
      } finally { setSaving(false); }
      return;
    }

    // Appel direct sans spot.
    setOpen(false);
    await startCall({ toNumber: e164, contactName: label || undefined, spotName: label || undefined });
  }, [valid, config, asSpot, f, e164, label, startCall, onSpotCreated]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size={size} variant={variant} data-testid={testId}
          className={variant === "default" ? `bg-[#1F9D55] text-white hover:bg-[#188045] ${triggerClassName}` : triggerClassName}>
          <PhoneCall className="mr-1.5 h-4 w-4" />{triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid="dialer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-[var(--shariti-orange)]" />Composer un numéro
          </DialogTitle>
        </DialogHeader>

        {!config?.ready && (
          <div className="flex items-start gap-2 rounded-xl border border-[rgba(240,190,20,0.6)] bg-[rgba(252,219,83,0.25)] px-3 py-2 text-[12px] text-[#5A4200]" data-testid="dialer-not-ready">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Téléphonie non active pour le moment (numéro Twilio en attente).
          </div>
        )}

        <div className="space-y-4">
          {/* Saisie du numéro */}
          <div>
            <Input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="06 12 34 56 78 ou +33…"
              inputMode="tel"
              className="h-12 text-center text-lg font-bold tracking-wide"
              data-testid="dialer-number-input"
            />
            <div className="mt-1 h-4 text-center text-[11px]">
              {number ? (
                valid
                  ? <span className="font-semibold text-[#1F9D55]" data-testid="dialer-e164-hint">Appel vers {prettyFr(e164)}</span>
                  : <span className="text-[#B23434]" data-testid="dialer-invalid-hint">Numéro incomplet ou invalide</span>
              ) : <span className="text-muted-ink">Saisissez un numéro français</span>}
            </div>
          </div>

          {/* Pavé numérique */}
          <div className="mx-auto grid max-w-[240px] grid-cols-3 gap-2">
            {PAD.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => pad(k)}
                data-testid={`dialer-pad-${k}`}
                className="grid h-11 place-items-center rounded-xl border border-[var(--border-soft)] bg-white text-lg font-bold text-ink transition-colors hover:bg-[var(--surface-2)] active:scale-95"
              >
                {k === "back" ? <Delete className="h-5 w-5 text-muted-ink" /> : k}
              </button>
            ))}
          </div>

          {/* Libellé rapide (si pas de spot) */}
          {!asSpot && (
            <div>
              <Label className="text-xs">Nom / enseigne (facultatif)</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="ex : M. Dupont — Franprix Voltaire" className="mt-1 h-9" data-testid="dialer-label-input" />
            </div>
          )}

          {/* Bascule : enregistrer comme nouveau spot */}
          <div className="flex items-center justify-between rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4 text-[var(--shariti-orange)]" />
              <div>
                <div className="text-[13px] font-bold text-ink">Enregistrer comme nouveau spot</div>
                <div className="text-[11px] text-muted-ink">Numéro hors base (directeur rencontré…)</div>
              </div>
            </div>
            <Switch checked={asSpot} onCheckedChange={setAsSpot} data-testid="dialer-as-spot-switch" />
          </div>

          {/* Formulaire spot complet */}
          {asSpot && (
            <div className="space-y-3 rounded-xl border border-[var(--border-soft)] p-3" data-testid="dialer-spot-form">
              <div className="grid grid-cols-2 gap-2.5">
                <div><Label className="text-xs">Enseigne *</Label><Input value={f.enseigne} onChange={setSpot("enseigne")} placeholder="Franprix" className="mt-1 h-9" data-testid="dialer-spot-enseigne" /></div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={f.type} onValueChange={setSpot("type")}>
                    <SelectTrigger className="mt-1 h-9" data-testid="dialer-spot-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supermarche">Supermarché</SelectItem>
                      <SelectItem value="galerie">Galerie</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Ville *</Label><Input value={f.ville} onChange={setSpot("ville")} placeholder="Paris" className="mt-1 h-9" data-testid="dialer-spot-ville" /></div>
                <div><Label className="text-xs">Arrondissement</Label><Input value={f.arrondissement} onChange={setSpot("arrondissement")} placeholder="11" className="mt-1 h-9" data-testid="dialer-spot-arr" /></div>
                <div><Label className="text-xs">Rue / Bd / Place</Label><Input value={f.rue} onChange={setSpot("rue")} placeholder="Voltaire" className="mt-1 h-9" data-testid="dialer-spot-rue" /></div>
                <div><Label className="text-xs">Nom donné</Label><Input value={f.nom_donne} onChange={setSpot("nom_donne")} placeholder="Bastille" className="mt-1 h-9" data-testid="dialer-spot-nom" /></div>
                <div><Label className="text-xs">Département</Label><Input value={f.dept} onChange={setSpot("dept")} placeholder="75" className="mt-1 h-9" data-testid="dialer-spot-dept" /></div>
                <div><Label className="text-xs">Flux clients/jour</Label><Input type="number" value={f.flux} onChange={setSpot("flux")} placeholder="800" className="mt-1 h-9" data-testid="dialer-spot-flux" /></div>
              </div>
              <div>
                <Label className="text-xs">Territoire</Label>
                <Select value={f.territoire_id} onValueChange={setSpot("territoire_id")}>
                  <SelectTrigger className="mt-1 h-9" data-testid="dialer-spot-territoire"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{territoires.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="border-t border-[var(--border-soft)] pt-2.5">
                <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-ink">Contact (directeur / responsable)</div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div><Label className="text-xs">Prénom</Label><Input value={f.prenom} onChange={setSpot("prenom")} placeholder="Marc" className="mt-1 h-9" data-testid="dialer-spot-prenom" /></div>
                  <div><Label className="text-xs">Nom</Label><Input value={f.nom} onChange={setSpot("nom")} placeholder="Dupont" className="mt-1 h-9" data-testid="dialer-spot-contact-nom" /></div>
                  <div><Label className="text-xs">Fonction</Label><Input value={f.fonction} onChange={setSpot("fonction")} placeholder="Directeur" className="mt-1 h-9" data-testid="dialer-spot-fonction" /></div>
                  <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={setSpot("email")} placeholder="marc@…" className="mt-1 h-9" data-testid="dialer-spot-email" /></div>
                </div>
                <p className="mt-1.5 text-[10px] text-muted-ink">Le numéro composé ({prettyFr(e164) || "—"}) sera enregistré comme téléphone du contact.</p>
              </div>

              <div><Label className="text-xs">Consignes spécifiques</Label><Textarea value={f.consignes} onChange={setSpot("consignes")} placeholder="ex : rester devant le magasin" className="mt-1 min-h-[60px] text-sm" data-testid="dialer-spot-consignes" /></div>
              {spotPreview && <div className="rounded-lg bg-[var(--surface-2)] px-3 py-2 text-xs"><span className="text-muted-ink">Nom normalisé : </span><span className="font-bold">{spotPreview}</span></div>}
            </div>
          )}

          {/* Action principale */}
          <Button onClick={() => launch(false)} disabled={!valid || saving || !config?.ready}
            className="h-11 w-full bg-[#1F9D55] text-base font-bold text-white hover:bg-[#188045] disabled:opacity-50" data-testid="dialer-call-btn">
            {saving ? <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              : asSpot ? <Sparkles className="mr-2 h-5 w-5" /> : <Phone className="mr-2 h-5 w-5" />}
            {asSpot ? "Créer le spot et appeler" : "Appeler"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
